/**
 * Desktop sidebar component with collapsible navigation
 * Includes prefetching on hover for instant page loads
 */

import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useStore } from '@/store';
import {
  Sidebar,
  SidebarHeader,
  SidebarBody,
  SidebarFooter,
  SidebarSection,
  SidebarItem,
  SidebarLabel,
  SidebarHeading,
  SidebarDivider,
} from '@/components/catalyst/sidebar';
import { Badge } from '@/components/catalyst/badge';
import { Tooltip } from '@/components/common/Tooltip';
import { WorkspaceSwitcher } from '@/components/workspace/WorkspaceSwitcher';
import { mainNavigation, settingsNavigation } from '@/config/navigation';
import { cn } from '@/lib/utils';
import { useOrganizationQuery } from '@/hooks/queries/use-organization-query';
import { calculateTrialStatus } from '@/features/billing/utils/trialHelpers';
import { TrialStatusCard } from '@/features/billing/components/TrialStatusCard';
import { usePrefetch } from '@/lib/query-client';

/**
 * Compact workspace indicator for collapsed sidebar
 */
function CompactWorkspace() {
  const currentWorkspace = useStore((s) => s.currentWorkspace);

  if (!currentWorkspace) return null;

  return (
    <Tooltip content={currentWorkspace.name} placement="right" delay={300}>
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
        {currentWorkspace.icon ? (
          <span className="text-lg">{currentWorkspace.icon}</span>
        ) : (
          <span className="text-sm font-semibold">
            {currentWorkspace.name.charAt(0).toUpperCase()}
          </span>
        )}
      </div>
    </Tooltip>
  );
}

export function DesktopSidebar() {
  const location = useLocation();
  const sidebarState = useStore((state) => state.sidebarState);
  const toggleSidebar = useStore((state) => state.toggleSidebar);
  const currentWorkspace = useStore((state) => state.currentWorkspace);
  const isCollapsed = sidebarState === 'collapsed';

  // Fetch organization data for trial status
  const { data: organization } = useOrganizationQuery();
  // Cast to OrganizationBilling for trial status calculation (Organization type includes billing fields)
  const trialStatus = calculateTrialStatus(
    organization as Parameters<typeof calculateTrialStatus>[0]
  );

  // Prefetching for instant page loads
  const { prefetchDashboard, prefetchSubmissions } = usePrefetch();

  const handleNavHover = useCallback(
    (href: string) => {
      if (!currentWorkspace?.id) return;

      // Prefetch data based on navigation target
      if (href === '/dashboard') {
        prefetchDashboard(currentWorkspace.id);
      } else if (href === '/submissions') {
        prefetchSubmissions(currentWorkspace.id);
      }
    },
    [currentWorkspace?.id, prefetchDashboard, prefetchSubmissions]
  );

  return (
    <aside
      className={cn(
        'fixed top-16 bottom-0 left-0 z-30 hidden overflow-x-hidden transition-all duration-200 lg:block',
        'border-r border-gray-200 bg-white dark:border-zinc-900 dark:bg-zinc-950',
        isCollapsed ? 'w-16' : 'w-60'
      )}
    >
      <Sidebar>
        {/* Workspace section */}
        <SidebarHeader>
          {isCollapsed ? (
            <div className="flex justify-center">
              <CompactWorkspace />
            </div>
          ) : (
            <WorkspaceSwitcher />
          )}
        </SidebarHeader>

        {/* Navigation */}
        <SidebarBody>
          {/* Main navigation with prefetching on hover */}
          <SidebarSection>
            {mainNavigation.items.map((item) => {
              const NavItem = (
                <div key={item.href} onMouseEnter={() => handleNavHover(item.href)}>
                  <SidebarItem href={item.href} current={location.pathname === item.href}>
                    <item.icon data-slot="icon" />
                    {!isCollapsed && (
                      <>
                        <SidebarLabel>{item.name}</SidebarLabel>
                        {item.badge && <Badge color="blue">{item.badge}</Badge>}
                      </>
                    )}
                  </SidebarItem>
                </div>
              );

              return isCollapsed ? (
                <Tooltip key={item.href} content={item.name} placement="right" delay={300}>
                  {NavItem}
                </Tooltip>
              ) : (
                NavItem
              );
            })}
          </SidebarSection>

          {/* Divider before settings when collapsed */}
          {isCollapsed && <SidebarDivider />}

          {/* Settings navigation - always visible */}
          {!isCollapsed && <SidebarHeading>{settingsNavigation.title}</SidebarHeading>}
          <SidebarSection>
            {settingsNavigation.items.map((item) => {
              const NavItem = (
                <SidebarItem
                  key={item.href}
                  href={item.href}
                  current={location.pathname === item.href}
                >
                  <item.icon data-slot="icon" />
                  {!isCollapsed && <SidebarLabel>{item.name}</SidebarLabel>}
                </SidebarItem>
              );

              return isCollapsed ? (
                <Tooltip key={item.href} content={item.name} placement="right" delay={300}>
                  {NavItem}
                </Tooltip>
              ) : (
                NavItem
              );
            })}
          </SidebarSection>
        </SidebarBody>

        {/* Trial Status & Collapse button */}
        <SidebarFooter>
          {!isCollapsed && trialStatus.isOnTrial && (
            <div className="mb-3">
              <TrialStatusCard status={trialStatus} />
            </div>
          )}

          <button
            onClick={toggleSidebar}
            className="w-full rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
          >
            {isCollapsed ? (
              <ChevronRightIcon className="mx-auto h-5 w-5" />
            ) : (
              <div className="flex items-center gap-2">
                <ChevronLeftIcon className="h-5 w-5" />
                <span className="text-sm">Collapse</span>
              </div>
            )}
          </button>
        </SidebarFooter>
      </Sidebar>
    </aside>
  );
}
